import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { useSite, whatsappLink } from "../context/SiteContext";
import { Reveal, SectionHead, Eyebrow } from "../components/Bits";
import { QuoteForm } from "../components/QuoteForm";

export default function Contact() {
  const { settings } = useSite();
  const c = settings?.contact || {};

  useSeo({
    title: "Request a Quote | Contact MAKO Mats",
    description:
      "Request a quote for combat mats and wall padding. Tell MAKO about your facility and we will help identify the right surface system.",
  });

  const items = [
    { Icon: Phone, label: "Telephone", value: c.phone, testId: "contact-phone" },
    { Icon: Mail, label: "Email", value: c.email, testId: "contact-email" },
    { Icon: MapPin, label: "Business location", value: c.address, testId: "contact-address" },
    { Icon: Clock, label: "Hours", value: c.hours, testId: "contact-hours" },
  ];

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#111111]/10 bg-white">
        <div className="mako-grid-bg">
          <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
            <Reveal>
              <Eyebrow>Request a quote</Eyebrow>
              <h1 className="mt-7 max-w-4xl font-display text-3xl font-bold uppercase leading-[1] tracking-[-0.02em] text-[#111111] sm:text-4xl lg:text-[3.5rem]">
                Tell us about your facility
              </h1>
              <p className="mt-8 max-w-2xl text-sm leading-relaxed text-[#3B3B3B] sm:text-base">
                Share the room, the disciplines you run and your timeline. We will help identify the
                right mat and wall-pad solution and come back with a quote.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <div className="lg:col-span-4">
            <div className="border border-[#111111]/10 bg-[#F5F5F2] p-8 lg:p-10">
              <p className="mako-label text-[#0F4C81]">Contact</p>
              <ul className="mt-8 space-y-7">
                {items.map(({ Icon, label, value, testId }) => (
                  <li key={label} className="flex gap-4">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" strokeWidth={1.75} />
                    <div>
                      <p className="mako-label text-[#7A7F85]">{label}</p>
                      <p className="mt-2 text-sm text-[#111111]" data-testid={testId}>
                        {value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <a
                href={whatsappLink(settings)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="mt-10 flex items-center justify-center gap-3 bg-[#111111] px-6 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#3B3B3B]"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp MAKO
              </a>

              <p className="mt-8 border-t border-[#111111]/10 pt-6 text-xs leading-relaxed text-[#7A7F85]">
                {c.coverage}
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <SectionHead eyebrow="Enquiry form" title="Request a quote" />
            <div className="mt-10">
              <QuoteForm source="contact-page" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
